import { SETTINGS_STORES_PATH } from "./tutorialRoutes";

const storeCta = {
  label: "Go to Settings",
  href: SETTINGS_STORES_PATH,
};

export const TUTORIAL_CONTENT = {
  dashboard: {
    pageName: "Dashboard",
    title: "Welcome to SparkSeller Connect",
    sections: [
      {
        heading: "What it is",
        body: "Your home base for Walmart Marketplace. After a store is linked, this page shows inventory, orders, Buy Box, and performance at a glance.",
      },
      {
        heading: "How to use it",
        body: "Pick a store, scan the cards and chart, then jump to a page with Quick Actions. You can customize those shortcuts later in Settings.",
      },
      {
        heading: "Set up a store",
        body: "Connect is built around a Walmart store. Open Settings, go to Walmart Stores, and click Add Store to sign in with Walmart (or enter API credentials).",
      },
    ],
    cta: storeCta,
  },
  settings: {
    pageName: "Settings",
    title: "Your account, stores, and team",
    sections: [
      {
        heading: "What it is",
        body: "This is where you manage appearance, profile, Walmart stores, billing, and teammates. Store setup lives in the Walmart Stores section below.",
      },
      {
        heading: "How to connect a store",
        body: "Click Add Store, then Connect with Walmart. Approve access in Seller Center. You can also paste a Client ID and Client Secret if you already have API credentials.",
      },
      {
        heading: "After you connect",
        body: "Test the connection, set a default store, and invite teammates if you have seats. Then open Connect pages like Listings, Inventory, and Orders.",
      },
    ],
    cta: { label: "Jump to Walmart Stores", href: SETTINGS_STORES_PATH },
  },
  items: {
    pageName: "Items",
    title: "Your internal product catalog",
    sections: [
      {
        heading: "What it is",
        body: "Items is SparkSeller’s catalog for cost, fees, and product details. It is separate from live Walmart listings.",
      },
      {
        heading: "How to use it",
        body: "Create items with the wizard, keep COGS up to date, and use this data so Profit & Loss and pricing have real costs behind them.",
      },
      {
        heading: "Set up a store",
        body: "You can build a catalog anytime. To publish or match live Walmart listings, connect a store in Settings first.",
      },
    ],
    cta: storeCta,
  },
  listings: {
    pageName: "Listings",
    title: "Manage live Walmart listings",
    sections: [
      {
        heading: "What it is",
        body: "A live view of your Walmart catalog: titles, prices, status, and SKUs pulled from Seller Center.",
      },
      {
        heading: "How to use it",
        body: "Sync listings from Walmart reports, edit details, retire items, run bulk price updates, or export a CSV.",
      },
      {
        heading: "Set up a store",
        body: "Listings stay locked until a Walmart store is connected. Add one in Settings → Walmart Stores.",
      },
    ],
    cta: storeCta,
  },
  inventory: {
    pageName: "Inventory",
    title: "Track and update stock",
    sections: [
      {
        heading: "What it is",
        body: "See WFS and seller-fulfilled quantities in one place, including what Walmart currently has on hand.",
      },
      {
        heading: "How to use it",
        body: "Edit seller-fulfilled qty, send bulk inventory feeds, and treat WFS quantities as Walmart-managed (they cannot be edited here).",
      },
      {
        heading: "Set up a store",
        body: "Inventory syncs from the connected store. Add a store in Settings if you have not already.",
      },
    ],
    cta: storeCta,
  },
  buybox: {
    pageName: "Buy Box",
    title: "See who is winning the Buy Box",
    sections: [
      {
        heading: "What it is",
        body: "Win/loss visibility from Walmart Buy Box reports so you know which SKUs you own and which you are losing.",
      },
      {
        heading: "How to use it",
        body: "Sync the latest report, filter by winner or SKU, then use Pricing or Listings to compete on price and content.",
      },
      {
        heading: "Set up a store",
        body: "Buy Box data comes from your Walmart store reports. Connect a store in Settings to unlock it.",
      },
    ],
    cta: storeCta,
  },
  orders: {
    pageName: "Orders",
    title: "Ship, acknowledge, and handle returns",
    sections: [
      {
        heading: "What it is",
        body: "Your Walmart order desk: open orders, returns, and return rules for the selected store.",
      },
      {
        heading: "How to use it",
        body: "Acknowledge new orders, ship with tracking, cancel when needed, and open a return to review or act on it.",
      },
      {
        heading: "Set up a store",
        body: "Orders pull from the connected Walmart account. Add a store in Settings to start receiving them here.",
      },
    ],
    cta: storeCta,
  },
  pricing: {
    pageName: "Pricing",
    title: "Update prices and run promotions",
    sections: [
      {
        heading: "What it is",
        body: "Price Updates, Promotions, Repricer, Pricing Insights, and Incentives — all in one Walmart pricing workspace.",
      },
      {
        heading: "How to use it",
        body: "Push price changes, schedule promotions, review insights, and use the repricer when you want rules to compete automatically.",
      },
      {
        heading: "Set up a store",
        body: "Pricing actions need a connected store. Set that up in Settings → Walmart Stores.",
      },
    ],
    cta: storeCta,
  },
  profit_loss: {
    pageName: "Profit & Loss",
    title: "See margin after real fees",
    sections: [
      {
        heading: "What it is",
        body: "Per-SKU profit and loss using your costs plus Walmart and WFS fees, so you can see what actually makes money.",
      },
      {
        heading: "How to use it",
        body: "Pick a store and date range, review margins, and update item costs on the Items page when a number looks off.",
      },
      {
        heading: "Set up a store",
        body: "P&L needs store sales and fee data. Connect a Walmart store in Settings, then keep COGS current in Items.",
      },
    ],
    cta: storeCta,
  },
  recon: {
    pageName: "Recon",
    title: "Reconcile Walmart payouts",
    sections: [
      {
        heading: "What it is",
        body: "Import Walmart settlement CSVs to see how payouts break down against what you sold.",
      },
      {
        heading: "How to use it",
        body: "Upload or run a settlement file, then review payout lines, fees, and exceptions. Use Run Now when you are ready to analyze.",
      },
      {
        heading: "Set up a store",
        body: "Recon is store-scoped. Connect a store in Settings, then pull or upload settlements here.",
      },
    ],
    cta: storeCta,
  },
  advertising: {
    pageName: "Advertising",
    title: "Review ad performance from CSVs",
    sections: [
      {
        heading: "What it is",
        body: "Upload Walmart advertising reports to track spend, ROAS, clicks, and how campaigns are performing.",
      },
      {
        heading: "How to use it",
        body: "Export the report from Walmart, upload it here, then sort and filter to find spend that is (or is not) paying back.",
      },
      {
        heading: "Set up a store",
        body: "Tie uploads to the right store by connecting it in Settings first.",
      },
    ],
    cta: storeCta,
  },
  campaigns: {
    pageName: "Campaigns",
    title: "Live SEM campaign performance",
    sections: [
      {
        heading: "What it is",
        body: "A live SEM overview with spend, sales, ROAS, impressions, and clicks, plus a daily spend-versus-sales chart and the campaign list underneath.",
      },
      {
        heading: "How to use it",
        body: "Stay on the SEM tab, pick a store and date range, then scan the cards and chart. Expand a campaign to load its items. Internal Walmart campaigns stay on the Walmart tab as a placeholder.",
      },
      {
        heading: "Set up a store",
        body: "Campaigns uses the selected store’s Walmart API credentials. Connect a store in Settings if this page is still locked.",
      },
    ],
    cta: storeCta,
  },
  insights: {
    pageName: "Insights",
    title: "Weekly Search Insights Performance",
    sections: [
      {
        heading: "What it is",
        body: "Walmart’s weekly Search Insights Performance report: item-level impressions, clicks, add-to-cart, and sales ranks plus 7-day in-stock, buy box win, and keyword recommendations. Previous-week data lands Wednesday at 7 AM PST.",
      },
      {
        heading: "How to use it",
        body: "Import the available week (Walmart publishes one week at a time), then pick a stored week or date range to calculate KPIs. Start with the action queue — SKUs that need keywords, leak from impressions to clicks, are low in stock, or are losing the buy box. Opportunity score is ours, built from those Walmart fields.",
      },
      {
        heading: "Set up a store",
        body: "Insights uses the selected store’s Walmart Marketplace credentials. Connect a store in Settings if this page is still locked.",
      },
    ],
    cta: storeCta,
  },
  marketing: {
    pageName: "Marketing",
    title: "Live Walmart Connect insights",
    sections: [
      {
        heading: "What it is",
        body: "A live look at Walmart Connect — trends and sponsored-ad context from the API, not just a CSV upload.",
      },
      {
        heading: "How to use it",
        body: "Select your store, review trends, and use this alongside Advertising when you want both live data and uploaded reports.",
      },
      {
        heading: "Set up a store",
        body: "Marketing needs a connected Walmart store (and advertiser mapping when required). Start in Settings → Walmart Stores.",
      },
    ],
    cta: storeCta,
  },
  compare: {
    pageName: "Compare",
    title: "Compare two time periods",
    sections: [
      {
        heading: "What it is",
        body: "Side-by-side P&L and advertising for two date ranges so you can see what changed.",
      },
      {
        heading: "How to use it",
        body: "Pick two periods, run the comparison, and look for SKUs or spend that moved in the wrong direction.",
      },
      {
        heading: "Set up a store",
        body: "Comparison uses the selected store’s data. Connect one in Settings if this page is still locked.",
      },
    ],
    cta: storeCta,
  },
  feeds: {
    pageName: "Feeds",
    title: "Track Walmart feed status",
    sections: [
      {
        heading: "What it is",
        body: "History and status for feeds you send to Walmart — inventory, pricing, and other bulk updates.",
      },
      {
        heading: "How to use it",
        body: "Open a feed to see whether Walmart accepted, processed, or rejected it, then fix errors and resend from the source page.",
      },
      {
        heading: "Set up a store",
        body: "Feeds are pulled for the connected store. Add a store in Settings to see feed history.",
      },
    ],
    cta: storeCta,
  },
  wfs_services: {
    pageName: "WFS Services",
    title: "Convert items to WFS inbound",
    sections: [
      {
        heading: "What it is",
        body: "Move seller-fulfilled items into Walmart Fulfillment Services by creating inbound conversion requests.",
      },
      {
        heading: "How to use it",
        body: "Select SF items, review the conversion details, and submit so Walmart can set them up for WFS inbound.",
      },
      {
        heading: "Set up a store",
        body: "WFS conversions need a connected store and eligible listings. Connect in Settings first.",
      },
    ],
    cta: storeCta,
  },
  wfs_estimator: {
    pageName: "WFS Estimator",
    title: "Estimate WFS fees before you send",
    sections: [
      {
        heading: "What it is",
        body: "A calculator for Walmart fulfillment fees and commissions so you can preview landed cost.",
      },
      {
        heading: "How to use it",
        body: "Enter dimensions, weight, and price to estimate fees. This page works even before a store is connected.",
      },
      {
        heading: "Set up a store",
        body: "Estimates are useful on their own. When you are ready to manage real WFS inventory, connect a store in Settings.",
      },
    ],
    cta: storeCta,
  },
  data_sync: {
    pageName: "Data Sync",
    title: "Keep Walmart data up to date",
    sections: [
      {
        heading: "What it is",
        body: "The control room for scheduled and manual pulls: listings, orders, inventory, recon, and CSV imports.",
      },
      {
        heading: "How to use it",
        body: "Run a sync now when you need fresh data, or set a schedule so reports and orders keep landing without extra clicks.",
      },
      {
        heading: "Set up a store",
        body: "Every sync is tied to a store. Connect one in Settings, then come back here to pull your first reports.",
      },
    ],
    cta: storeCta,
  },
};

export function getTutorialContent(pageId) {
  return TUTORIAL_CONTENT[pageId] || null;
}
